import { registerActions } from "../actionRegistry";
/**
 * Partner and Dashboard management functions
 */

import {
  getDomElement,
  getDomElementOrThrow,
  setHtml,
  queryAll,
  idSuffix,
  fadeIn,
  fadeOut,
} from "../utils/dom";
import { config } from "../config";

/**
 * Every `.dashboard-compact` row paired with its raw partner/artist id. The row
 * element IS `#partnercompact-<id>`, so the legacy `$("#partnercompact-" + raw_id)`
 * lookups just resolved back to the row already in hand.
 */
function compactRows(): { el: HTMLElement; id: string }[] {
  return Array.from(queryAll<HTMLElement>(".dashboard-compact"), (el) => {
    if (!el.id) throw new Error("Expected element to have an id attribute");
    return { el, id: idSuffix(el, "partnercompact-") };
  });
}

/** Reset every row to visible (the "table-row" default these rows are laid out with). */
function showAllCompactRows(): void {
  for (const { el } of compactRows()) el.style.display = "table-row";
}

/** Toggle the `#toggle-selectall` / `#toggle-<id>` checkbox icon. */
function setToggleIcon(toggleId: string, checked: boolean): void {
  const icon = getDomElement(toggleId);
  if (!icon) return;
  if (checked) {
    icon.classList.remove("far", "fa-square");
    icon.classList.add("fas", "fa-check-square");
  } else {
    icon.classList.remove("fas", "fa-check-square");
    icon.classList.add("far", "fa-square");
  }
}

/**
 * Toggle partner selection
 */
export function togglePartner(id: string): void {
  const stateEl = getDomElement<HTMLInputElement>("state-" + id);
  if (stateEl) {
    const selected = stateEl.value == "0";
    stateEl.value = selected ? "1" : "0";
    setToggleIcon("toggle-" + id, selected);
    getDomElement("partnercompact-" + id)?.classList.toggle("dashboard-compact-selected", selected);
  }
  togglePartnerParse();
}

/**
 * Parse selected partners and update UI
 */
export function togglePartnerParse(): void {
  const selectedIds = compactRows()
    .filter(({ id }) => getDomElement<HTMLInputElement>("state-" + id)?.value == "1")
    .map(({ id }) => id);
  const selected = selectedIds.length;
  const selected_str = selectedIds.join(",");

  if (selected > 0) {
    const partner_s = selected == 1 ? "" : "s";
    // The spans carry data-action="modal" so actionDelegator dispatches them —
    // the old code hand-bound a click handler per span because the bare
    // data-modal attribute alone is not something the delegator listens for.
    const isArtists = getDomElement("dashboard-artists") != null;
    const noun = isArtists ? "artist" : "partner";
    const newsletterModal = isArtists ? "sendnewsletter-artist" : "sendnewsletter";
    const groupsModal = isArtists ? "addtoartistgroups" : "addtopartnergroups";
    const groupsLabel = isArtists ? "Add to Artist Groups" : "Add to Partner Groups";
    setHtml(
      "dashboard-floater",
      `<div class="dashboard-floater-header">${selected} ${noun}${partner_s} selected</div>` +
        `<div class="dashboard-floater-subheader">Perform multi actions on selected ${noun}s</div>` +
        `<div class="dashboard-floater-buttons"><span class="button medium green" data-action="modal" data-modal="${newsletterModal}" data-size="large"><i class="fa fa-envelope"></i> Send newsletter</span></div>` +
        `<div class="dashboard-floater-buttons"><span class="button medium yellow" data-action="modal" data-modal="${groupsModal}" data-size="dynamic"><i class="fas fa-users"></i> ${groupsLabel}</span></div>`
    );
    fadeIn("dashboard-floater");
  } else {
    fadeOut("dashboard-floater");
  }
  const selectedPartnersEl = getDomElement<HTMLInputElement>("selectedpartners");
  if (selectedPartnersEl) {
    selectedPartnersEl.value = selected_str;
  }
}

/**
 * Select all partners
 */
export function partnerSelectAll(): void {
  const selectAllEl = getDomElementOrThrow<HTMLInputElement>("state-selectall");
  if (selectAllEl.value == "0") {
    selectAllEl.value = "1";
    setToggleIcon("toggle-selectall", true);
    for (const { el, id } of compactRows()) {
      const stateEl = getDomElement<HTMLInputElement>("state-" + id);
      if (stateEl?.value == "0" && el.style.display != "none") {
        togglePartner(id);
      }
    }
  } else {
    selectAllEl.value = "0";
    setToggleIcon("toggle-selectall", false);
    for (const { id } of compactRows()) {
      if (getDomElement<HTMLInputElement>("state-" + id)?.value == "1") {
        togglePartner(id);
      }
    }
  }
}

/**
 * Select partners by type
 */
function eachPartner(predicate: (raw_id: string) => boolean): void {
  for (const { el, id } of compactRows()) {
    if (el.style.display != "none" && predicate(id)) {
      togglePartner(id);
    }
  }
}

export function partnerselect(what: string): void {
  const selectAllEl = getDomElement<HTMLInputElement>("state-selectall");
  if (selectAllEl) {
    selectAllEl.value = "0";
  }
  setToggleIcon("toggle-selectall", false);

  // Deselect all first
  for (const { id } of compactRows()) {
    if (getDomElement<HTMLInputElement>("state-" + id)?.value == "1") {
      togglePartner(id);
    }
  }

  if (what == "invited") {
    eachPartner((id) => getDomElement("invited-" + id) != null);
  }

  if (what == "inviteddl") {
    eachPartner((id) => {
      if (getDomElement("invited-" + id) == null) return false;
      const el = getDomElement("downloaded-" + id);
      return !!el && parseInt(el.innerHTML) > 0;
    });
  }

  if (what == "invitednodl") {
    eachPartner((id) => {
      if (getDomElement("invited-" + id) == null) return false;
      const el = getDomElement("downloaded-" + id);
      return !!el && parseInt(el.innerHTML) == 0;
    });
  }

  if (what == "invitedfewdl") {
    eachPartner((id) => {
      if (getDomElement("invited-" + id) == null) return false;
      const el = getDomElement("downloaded-" + id);
      return !!el && parseInt(el.innerHTML) < 50;
    });
  }

  if (what == "artistnoul") {
    eachPartner((id) => {
      const el = getDomElement("uploaded-" + id);
      return !!el && parseInt(el.innerHTML) == 0 && getDomElement("terminated-" + id) == null;
    });
  }

  if (what == "artistul") {
    eachPartner((id) => {
      const el = getDomElement("uploaded-" + id);
      return !!el && parseInt(el.innerHTML) > 0 && getDomElement("terminated-" + id) == null;
    });
  }
}

/**
 * Filter by group — shared body of onlygroup()/onlygroupartist(), which differ
 * only in which "only mine" checkbox they clear and disable.
 *
 * Note the row-hiding in the `onlyMineEl.checked` branch is immediately undone by
 * showAllCompactRows() below; that is the pre-existing behaviour, kept as-is.
 */
function filterByGroup(id: string | number, onlyMineId: string): void {
  const onlyMineEl = getDomElement<HTMLInputElement>(onlyMineId);
  if (onlyMineEl?.checked) {
    for (const { el, id: raw_id } of compactRows()) {
      if (getDomElement("mine-" + raw_id) == null) el.style.display = "none";
    }
    onlyMineEl.checked = false;
  }
  onlyMineEl?.setAttribute("readonly", "readonly");
  onlyMineEl?.setAttribute("disabled", "disabled");

  showAllCompactRows();

  if (parseInt(String(id)) > 0) {
    for (const { el, id: raw_id } of compactRows()) {
      if (getDomElement("group-" + id + "-" + raw_id) == null) el.style.display = "none";
    }
  } else {
    onlyMineEl?.removeAttribute("disabled");
    onlyMineEl?.removeAttribute("readonly");
  }
}

export function onlygroup(id: string | number): void {
  filterByGroup(id, "onlymine");
}

/**
 * Filter by artist group
 */
export function onlygroupartist(id: string | number): void {
  filterByGroup(id, "onlymineartist");
}

/**
 * Filter to show only mine
 */
export function onlymine(): void {
  if (getDomElement<HTMLInputElement>("onlymine")?.checked) {
    hideRowsWhereNotMine("owner-");
  } else {
    showAllCompactRows();
  }
}

/** Hide every row whose `<prefix><id>` input doesn't hold the current user's id. */
function hideRowsWhereNotMine(prefix: string): void {
  for (const { el, id } of compactRows()) {
    const ownerEl = getDomElement<HTMLInputElement>(prefix + id);
    if (ownerEl && ownerEl.value != String(config.authUser)) el.style.display = "none";
  }
}

/**
 * Filter to show only my artists
 */
export function onlymineartist(): void {
  showAllCompactRows();

  const require_bio = getDomElement<HTMLInputElement>("onlybio")?.checked === true;
  const mine = getDomElement<HTMLInputElement>("onlymineartist")?.checked === true;
  const signed = getDomElement<HTMLInputElement>("onlymineartistsigned")?.checked === true;

  if (mine && !signed) {
    hideRowsWhereNotMine("owner-");
  }
  if (signed && !mine) {
    hideRowsWhereNotMine("signedby-");
  }
  if (signed && mine) {
    // Both checked: a row survives only if BOTH signedby and owner are me.
    for (const { el, id } of compactRows()) {
      const signedByEl = getDomElement<HTMLInputElement>("signedby-" + id);
      const ownerEl = getDomElement<HTMLInputElement>("owner-" + id);
      if (
        (signedByEl && signedByEl.value != String(config.authUser)) ||
        (ownerEl && ownerEl.value != String(config.authUser))
      ) {
        el.style.display = "none";
      }
    }
  }

  if (require_bio) {
    for (const { el, id } of compactRows()) {
      if (getDomElement("hasbio-" + id) == null) el.style.display = "none";
    }
  }
}

registerActions({
  togglePartner,
  togglePartnerParse,
  partnerSelectAll,
  partnerselect,
  onlygroup,
  onlygroupartist,
  onlymine,
  onlymineartist,
});
